import makeStyles from '@mui/styles/makeStyles';
import { AppAvatar, brandclub_colors } from '@stackline/ui';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { FC } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SelectRetailerDropdown from 'src/components/BeaconV2Components/Header/SelectRetailerDropdown';
import SearchInput from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { BEACON_PRO_CENTER_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { AtlasLogo } from 'src/components/SvgIcons/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';
import { useAppSubscribed } from 'src/utils/Hooks/reduxSelectors';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { GlobalComponents } from 'src/components/BeaconV2Components/Header/GlobalComponents/GlobalComponents';
import { useAtlasV2HomeLink } from 'src/components/AtlasV2Components/AtlasV2LayoutComponents/Header/hooks';
import { redirectAdvertising } from 'src/utils/app';
import AccountDivisionPopover from 'src/components/BeaconV2Components/Header/AccountDivisionPopover';

const useStyles = makeStyles({
  header: {
    height: 60,
    backgroundColor: brandclub_colors.blueWhite,
    position: 'initial',
    top: 0,
    // z-index must be higher than AtlasV2PrimaryNav (1000) so the
    // Advisor suggested-questions dropdown can overlay the nav bar.
    zIndex: 1001
  },
  centerDiv: {
    margin: '0  auto',
    width: BEACON_PRO_CENTER_WIDTH,
    boxSizing: 'border-box',
    height: 60,
    display: 'grid',
    gridTemplateColumns: `1fr 400px 1fr`,
    alignItems: 'center'
  },
  productLogo: {
    height: 30,
    width: 30,
    marginLeft: '-1px',
    '& :hover': {
      cursor: 'pointer'
    }
  },
  userOptionsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px'
  },
  companyName: {
    fontSize: '1.4rem',
    fontWeight: 500,
    lineHeight: 1.43,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  settingsIconButton: {
    height: 24,
    width: 24,
    padding: 0,
    margin: '0 26px 0 20px',
    borderRight: '1ps solid black'
  },
  accountIconButton: {
    height: 30,
    width: 30,
    padding: 0
  },
  accountIcon: {
    height: 36,
    width: 36
  },
  // drawer
  drawerButtonContainer: {
    textAlign: 'right'
  },
  drawerButtonRoot: {
    paddingRight: 0
  },
  menuIcon: {
    transform: 'scale(1.4, 1.7)'
  },
  menuCloseIcon: {
    transform: 'scale(1.5)'
  },
  drawerRoot: {
    fontFamily: 'Roboto'
  },
  drawerPaperRoot: {
    top: 60,
    left: 0,
    padding: 20
  },
  drawerCompanyName: {
    color: brandclub_colors.darkBlue,
    fontSize: '1.8rem',
    fontWeight: 'bold',
    lineHeight: 1.43,
    marginTop: 10,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  listRoot: {
    color: brandclub_colors.darkBlue
  },
  listItemRoot: {
    padding: '20px 0',
    '& a': {
      color: brandclub_colors.darkBlue,
      textDecoration: 'none'
    }
  }
});

// -------------------------------------------------------------

interface HeaderProps extends RouteComponentProps {
  appName: string;
}

const AtlasV2Header: FC<HeaderProps> = ({ location, history, appName }) => {
  const classes = useStyles();
  const AtlasV2HomeLink = useAtlasV2HomeLink();
  const APP_DETAILS = useAppSubscribed();
  const beaconClientId = useAppSelector((state) => state?.user?.config?.vendor?.BeaconClientId);
  const noDrive = redirectAdvertising(beaconClientId?.toString());

  const appDropdownOption = Object.keys(APP_DETAILS)
    .map((appName) => {
      return {
        ...APP_DETAILS[appName],
        label: APP_DETAILS[appName].getLabel(),
        disabled: !APP_DETAILS[appName].shouldBeEnabled()
      };
    })
    .filter((option) => {
      if (option.id === 'omni') {
        return false;
      }
      if (noDrive) {
        return option.id !== 'drive';
      }
      return true;
    });

  return (
    <div className={classes.header}>
      <div className={classes.centerDiv}>
        <Flex alignItems="center">
          <GlobalComponents />
          <AppAvatar
            isRawSrc
            variant="square"
            sx={{ backgroundColor: 'transparent' }}
            classes={{
              root: classes.productLogo
            }}
            imgProps={{
              style: {
                objectFit: 'contain'
              }
            }}
            onClick={() => {
              if (`${location.pathname + location.search}` !== AtlasV2HomeLink) {
                history.push(AtlasV2HomeLink);
              }
            }}
          >
            <AtlasLogo />
          </AppAvatar>

          <SlDropdownMenu
            options={appDropdownOption}
            defaultLabel="Atlas Pro"
            selectedId="atlas"
            onChange={(selectedApp: SlDropdownMenuOption & { path: string }) => {
              if (selectedApp.id !== 'atlas') {
                window.open(selectedApp.path, '_blank');
              }
            }}
            buttonVariant="titleSmall"
            iconStyle={{ marginLeft: '8px' }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            menuStyles={{ width: '151px' }}
            slMenuProps={{
              fixSelectedId: false,
              sx: {
                '& .MuiMenuItem-root': {
                  paddingRight: '12px'
                }
              }
            }}
          />
        </Flex>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SearchInput />
        </div>
        <div>
          <div className={classes.userOptionsDiv}>
            {!window.location.href.includes('entityType=shelf') ? <SelectRetailerDropdown /> : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                  <AccountDivisionPopover appName="atlas" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(AtlasV2Header);
